import { AvailableLanguageInterface } from '../exports/Interfaces';
import { LANGUAGE_NAMES, LANGUAGE_CODES as REGISTRY_CODES } from '../../resources/lang/languages.generated';

/**
 * Every language this build ships a catalog for, from the generated registry — add a
 * language to scripts/translate-catalog.config.json and re-run the script, never here.
 *
 * `languageName` is the endonym on purpose — a picker lists each language in its
 * own language so a speaker can find theirs without first being able to read the
 * surrounding UI.
 */
export const AVAILABLE_LANGUAGES: AvailableLanguageInterface[] = REGISTRY_CODES.map((language) => ({
  language,
  languageName: LANGUAGE_NAMES[language as keyof typeof LANGUAGE_NAMES],
}));

export const LANGUAGE_CODES = AVAILABLE_LANGUAGES.map((item) => item.language);

// What an untranslated key resolves to.
export const FALLBACK_LANGUAGE = 'en';

/**
 * A locale tag reduced to the language this app keys its catalogs by.
 *
 * i18next's own `load: 'languageOnly'` splits on '-' only, so an underscore form such
 * as `pl_PL` reaches resource lookup intact, misses every bundle and renders through
 * the English fallback. Underscore forms are not hypothetical — the storefront writes
 * its locale straight into the hand-off cookie, and `?lng=` is whatever the caller put
 * there — so anything arriving from outside this app is normalised here first.
 */
export function baseLanguage(language?: string | null): string | null {
  const base = language?.split(/[-_]/)[0]?.toLowerCase();

  return base || null;
}

/**
 * Languages the platform can actually send mail in.
 *
 * Deliberately narrower than LANGUAGE_CODES, and it must stay that way until the mail
 * templates catch up. This app's catalogs are bundled JS, but mail is rendered
 * elsewhere: transactional mail by the backend (`resources/lang`, de + en only) and
 * marketing mail by foodamigos-marketing, whose AppLocalizer replaces the English
 * fallback with the customer's own language. Hand either of them a code they have no
 * templates for and `trans()` returns the key — the customer receives `subjects.*` as
 * the subject line of their OTP, loyalty or members-club mail.
 */
export const MAILABLE_LANGUAGE_CODES = ['de', 'en'];

/**
 * The language to hand the backend for anything that ends up in an email, or null when
 * the customer's UI language is one mail cannot render. Null makes the backend fall
 * back to the restaurant's own legal language, which is always a language it ships.
 *
 * Every `lang` this app sends goes through here. Before the nine-language rollout
 * i18n.language could only be de or en, so passing it straight through was safe; it no
 * longer is, and the failure is silent on this side of the wire.
 */
export function mailableLanguage(language?: string | null): string | null {
  const base = baseLanguage(language);

  return base && MAILABLE_LANGUAGE_CODES.includes(base) ? base : null;
}

/**
 * The languages the customer picker may offer.
 *
 * `supported` is the active list from the API (`company.supported_languages`).
 * Anything it names that this build has no catalog for is dropped, so activating a
 * language server-side can never point the UI at strings that aren't in the bundle.
 * An absent list means the API predates the field — fall back to everything shipped
 * rather than emptying the picker.
 */
export function getActiveLanguages(supported?: string[] | null): AvailableLanguageInterface[] {
  if (!supported?.length) {
    return AVAILABLE_LANGUAGES;
  }

  return AVAILABLE_LANGUAGES.filter((item) => supported.includes(item.language));
}
